import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { convertToXml, loadECMRFromLocalStorage, saveECMRAsXml } from '../services/eCMRService';
import { ECMR, DigitalSignature } from '../types/eCMR';

const ViewECMR: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [ecmr, setEcmr] = useState<ECMR | null>(null);
  const [xmlView, setXmlView] = useState<string>('');
  const [viewMode, setViewMode] = useState<'formatted' | 'xml'>('formatted');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);

  useEffect(() => {
    // Verificar si hay advertencias de validación en el estado de navegación
    const state = location.state as { validationWarnings?: string[] } | undefined;
    if (state?.validationWarnings) {
      setValidationWarnings(state.validationWarnings);
    }
    
    if (!id) {
      setError('ID de e-CMR no proporcionado');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const loadedEcmr = loadECMRFromLocalStorage(id);
        if (!loadedEcmr) {
          setError('e-CMR no encontrado');
          setLoading(false);
          return;
        }

        setEcmr(loadedEcmr);
        // Convertir a XML para la vista XML
        const xml = await convertToXml(loadedEcmr);
        setXmlView(xml);
        setLoading(false);
      } catch (err) {
        setError(`Error al cargar e-CMR: ${(err as Error).message}`);
        setLoading(false);
      }
    };

    loadData();
  }, [id, location.state]);

  const handleExportXml = async () => {
    if (!ecmr) return;
    
    try {
      const filename = ecmr.ExchangedDocument?.ID 
        ? `ecmr_${ecmr.ExchangedDocument.ID}.xml` 
        : 'ecmr.xml';
        
      await saveECMRAsXml(ecmr, filename);
    } catch (err) {
      setError(`Error al exportar XML: ${(err as Error).message}`);
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return 'Fecha no disponible';
    
    // Format: YYYYMMDDHHMMSS
    if (dateStr.length === 14) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      const hour = dateStr.substring(8, 10);
      const minute = dateStr.substring(10, 12);
      
      return `${day}/${month}/${year} ${hour}:${minute}`;
    }
    
    return dateStr;
  };

  const hasAnySignature = (): boolean => {
    return !!(
      ecmr?.ExchangedDocument?.ConsignorDigitalSignature ||
      ecmr?.ExchangedDocument?.CarrierDigitalSignature ||
      ecmr?.ExchangedDocument?.ConsigneeDigitalSignature
    );
  };

  const renderSignatureBlock = (title: string, signature?: DigitalSignature) => {
    if (!signature) return (
      <div className="p-4 border border-gray-300 rounded bg-gray-100">
        <h4 className="font-medium text-lg mb-2">{title}</h4>
        <p className="text-gray-500 italic">Firma pendiente</p>
      </div>
    );
    
    // Format date
    let formattedDate = signature.SigningTime;
    try {
      const dateObj = new Date(signature.SigningTime);
      formattedDate = dateObj.toLocaleString();
    } catch (e) {
      // Keep original format if error
    }
    
    return (
      <div className="p-4 border border-green-300 rounded bg-green-50">
        <h4 className="font-medium text-lg mb-2">{title}</h4>
        <p className="text-sm mb-2">
          <span className="font-medium">Fecha: </span>
          {formattedDate}
        </p>
        <div className="mt-2">
          {signature.SignatureValue && (
            <div className="border p-2 bg-white">
              <img 
                src={signature.SignatureValue} 
                alt={`Firma de ${title}`} 
                className="max-h-24 w-auto"
                style={{ maxWidth: '100%' }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center">
          <p className="text-lg">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error}</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Volver a la lista
        </button>
      </div>
    );
  }

  if (!ecmr) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
          <p>No se encontró el documento e-CMR</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Volver a la lista
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          e-CMR: {ecmr.ExchangedDocument?.ID || 'Sin título'}
        </h1>
        
        <div className="flex space-x-2">
          <button
            onClick={() => setViewMode(viewMode === 'formatted' ? 'xml' : 'formatted')}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
          >
            {viewMode === 'formatted' ? 'Ver XML' : 'Ver Formato'}
          </button>
          
          <button
            onClick={handleExportXml}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          >
            Exportar XML
          </button>
          
          <Link
            to={`/edit/${id}`}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Editar
          </Link>
          
          <button
            onClick={() => navigate('/')}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
          >
            Volver
          </button>
        </div>
      </div>
      
      {/* Mostrar advertencias de validación si existen */}
      {validationWarnings.length > 0 && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
          <h3 className="font-bold">Advertencias de validación:</h3>
          <ul className="list-disc pl-5">
            {validationWarnings.map((warning, index) => (
              <li key={index} className="text-sm">{warning}</li>
            ))}
          </ul>
          <p className="text-sm mt-2">Este documento puede no cumplir completamente con el estándar e-CMR.</p>
        </div>
      )}
      
      {viewMode === 'xml' ? (
        <div className="bg-white shadow-md rounded p-4 overflow-auto text-sm">
          <pre className="whitespace-pre-wrap">{xmlView}</pre>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded p-6">
          {/* Información general del documento */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Información del Documento</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-medium">ID:</p>
                <p>{ecmr.ExchangedDocument?.ID || 'N/A'}</p>
              </div>
              <div>
                <p className="font-medium">Fecha de emisión:</p>
                <p>{formatDate(ecmr.ExchangedDocument?.IssueDateTime?.DateTimeString)}</p>
              </div>
              <div>
                <p className="font-medium">Lugar de emisión:</p>
                <p>{ecmr.ExchangedDocument?.IssueLogisticsLocation?.Name || 'N/A'}</p>
              </div>
              <div>
                <p className="font-medium">Observaciones:</p>
                <p>{Array.isArray(ecmr.ExchangedDocument?.Remarks) ? (ecmr.ExchangedDocument?.Remarks?.join(', ')) : (ecmr.ExchangedDocument?.Remarks || 'N/A')}</p>
              </div>
            </div>
          </div>

          {/* Información de las partes */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Partes Involucradas</h2>
            
            {/* Remitente */}
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Remitente</h3>
              <div className="grid grid-cols-2 gap-4 pl-4">
                <div>
                  <p className="font-medium">Nombre:</p>
                  <p>{ecmr.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.Name?.[0] || 'N/A'}</p>
                </div>
                <div>
                  <p className="font-medium">ID:</p>
                  <p>{ecmr.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.ID?.[0] || 'N/A'}</p>
                </div>
                <div>
                  <p className="font-medium">Dirección:</p>
                  <p>
                    {ecmr.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.PostalTradeAddress?.StreetName?.[0]}, {' '}
                    {ecmr.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.PostalTradeAddress?.CityName?.[0]}, {' '}
                    {ecmr.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.PostalTradeAddress?.CountryID || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="font-medium">Contacto:</p>
                  <p>
                    {ecmr.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.DefinedTradeContact?.[0]?.PersonName?.[0] || 'N/A'}, {' '}
                    {ecmr.SpecifiedSupplyChainConsignment.ConsignorTradeParty?.DefinedTradeContact?.[0]?.TelephoneUniversalCommunication?.[0]?.CompleteNumber || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Destinatario */}
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Destinatario</h3>
              <div className="grid grid-cols-2 gap-4 pl-4">
                <div>
                  <p className="font-medium">Nombre:</p>
                  <p>{ecmr.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.Name?.[0] || 'N/A'}</p>
                </div>
                <div>
                  <p className="font-medium">ID:</p>
                  <p>{ecmr.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.ID?.[0] || 'N/A'}</p>
                </div>
                <div>
                  <p className="font-medium">Dirección:</p>
                  <p>
                    {ecmr.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.PostalTradeAddress?.StreetName?.[0]}, {' '}
                    {ecmr.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.PostalTradeAddress?.CityName?.[0]}, {' '}
                    {ecmr.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.PostalTradeAddress?.CountryID || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="font-medium">Contacto:</p>
                  <p>
                    {ecmr.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.DefinedTradeContact?.[0]?.PersonName?.[0] || 'N/A'}, {' '}
                    {ecmr.SpecifiedSupplyChainConsignment.ConsigneeTradeParty?.DefinedTradeContact?.[0]?.TelephoneUniversalCommunication?.[0]?.CompleteNumber || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Transportista */}
            <div>
              <h3 className="text-lg font-medium mb-2">Transportista</h3>
              <div className="grid grid-cols-2 gap-4 pl-4">
                <div>
                  <p className="font-medium">Nombre:</p>
                  <p>{ecmr.SpecifiedSupplyChainConsignment.CarrierTradeParty?.Name?.[0] || 'N/A'}</p>
                </div>
                <div>
                  <p className="font-medium">ID:</p>
                  <p>{ecmr.SpecifiedSupplyChainConsignment.CarrierTradeParty?.ID?.[0] || 'N/A'}</p>
                </div>
                <div>
                  <p className="font-medium">Dirección:</p>
                  <p>
                    {ecmr.SpecifiedSupplyChainConsignment.CarrierTradeParty?.PostalTradeAddress?.StreetName?.[0]}, {' '}
                    {ecmr.SpecifiedSupplyChainConsignment.CarrierTradeParty?.PostalTradeAddress?.CityName?.[0]}, {' '}
                    {ecmr.SpecifiedSupplyChainConsignment.CarrierTradeParty?.PostalTradeAddress?.CountryID || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="font-medium">Contacto:</p>
                  <p>
                    {ecmr.SpecifiedSupplyChainConsignment.CarrierTradeParty?.DefinedTradeContact?.[0]?.PersonName?.[0] || 'N/A'}, {' '}
                    {ecmr.SpecifiedSupplyChainConsignment.CarrierTradeParty?.DefinedTradeContact?.[0]?.TelephoneUniversalCommunication?.[0]?.CompleteNumber || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Información de la mercancía */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Información de la Mercancía</h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="font-medium">Peso bruto:</p>
                <p>
                  {ecmr.SpecifiedSupplyChainConsignment.GrossWeightMeasure?.[0]?.value || 'N/A'} {' '}
                  {ecmr.SpecifiedSupplyChainConsignment.GrossWeightMeasure?.[0]?.unitCode || ''}
                </p>
              </div>
              <div>
                <p className="font-medium">Volumen bruto:</p>
                <p>
                  {ecmr.SpecifiedSupplyChainConsignment.GrossVolumeMeasure?.[0]?.value || 'N/A'} {' '}
                  {ecmr.SpecifiedSupplyChainConsignment.GrossVolumeMeasure?.[0]?.unitCode || ''}
                </p>
              </div>
            </div>
            
            <h3 className="text-lg font-medium mb-2">Artículos</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nº</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Peso</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paquetes</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ecmr.SpecifiedSupplyChainConsignment.IncludedSupplyChainConsignmentItem?.map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap">{item.SequenceNumeric}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.GrossWeightMeasure?.value || 'N/A'} {item.GrossWeightMeasure?.unitCode || ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {Array.isArray(item.TransportLogisticsPackage) 
                          ? item.TransportLogisticsPackage.map(pkg => pkg.ItemQuantity || 0).reduce((sum, qty) => sum + qty, 0) 
                          : ((item.TransportLogisticsPackage as any)?.ItemQuantity || 'N/A')}
                      </td>
                      <td className="px-6 py-4">
                        {Array.isArray(item.TransportLogisticsPackage) 
                          ? item.TransportLogisticsPackage.map(pkg => pkg.Description || 'N/A').join(', ') 
                          : ((item.TransportLogisticsPackage as any)?.Description || 'N/A')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Información de transporte */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Información de Transporte</h2>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="font-medium">Lugar de recogida:</p>
                <p>{ecmr.SpecifiedSupplyChainConsignment.CarrierAcceptanceLogisticsLocation?.Name || 'N/A'}</p>
              </div>
              <div>
                <p className="font-medium">Lugar de entrega:</p>
                <p>{ecmr.SpecifiedSupplyChainConsignment.ConsigneeReceiptLogisticsLocation?.Name || 'N/A'}</p>
              </div>
              <div>
                <p className="font-medium">Fecha de recogida:</p>
                <p>{formatDate(ecmr.SpecifiedSupplyChainConsignment.PickUpTransportEvent?.ScheduledOccurrenceDateTime?.DateTimeString)}</p>
              </div>
              <div>
                <p className="font-medium">Fecha de entrega:</p>
                <p>{formatDate(ecmr.SpecifiedSupplyChainConsignment.DeliveryTransportEvent?.ScheduledOccurrenceDateTime?.DateTimeString)}</p>
              </div>
            </div>

            {ecmr.SpecifiedSupplyChainConsignment.MainCarriageLogisticsTransportMovement && 
             ecmr.SpecifiedSupplyChainConsignment.MainCarriageLogisticsTransportMovement.length > 0 && (
              <div>
                <h3 className="text-lg font-medium mb-2">Vehículo</h3>
                <div className="pl-4">
                  <p>
                    <span className="font-medium">ID: </span>
                    {ecmr.SpecifiedSupplyChainConsignment.MainCarriageLogisticsTransportMovement[0]?.UsedLogisticsTransportMeans?.ID || 'N/A'}
                  </p>
                  <p>
                    <span className="font-medium">Tipo: </span>
                    {ecmr.SpecifiedSupplyChainConsignment.MainCarriageLogisticsTransportMovement[0]?.UsedLogisticsTransportMeans?.Name || 'N/A'}
                  </p>
                  <p>
                    <span className="font-medium">Conductor: </span>
                    {ecmr.SpecifiedSupplyChainConsignment.MainCarriageLogisticsTransportMovement[0]?.DriverPerson?.Name || 'N/A'}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Instrucciones y términos de entrega */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Instrucciones y Términos</h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-medium">Instrucciones de manipulación:</p>
                <p>{Array.isArray(ecmr.SpecifiedSupplyChainConsignment.HandlingInstructions?.[0]?.Description) ? (ecmr.SpecifiedSupplyChainConsignment.HandlingInstructions?.[0]?.Description?.join(', ')) : (ecmr.SpecifiedSupplyChainConsignment.HandlingInstructions?.[0]?.Description || 'N/A')}</p>
              </div>
              <div>
                <p className="font-medium">Términos de entrega:</p>
                <p>{ecmr.SpecifiedSupplyChainConsignment.SpecifiedTradeDeliveryTerms?.DeliveryTypeCode || 'N/A'}</p>
              </div>
              <div>
                <p className="font-medium">Transbordo permitido:</p>
                <p>{ecmr.SpecifiedSupplyChainConsignment.TransshipmentPermissionIndicator ? 'Sí' : 'No'}</p>
              </div>
            </div>
          </div>

          {/* Sección de Firmas Digitales */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4 border-b-2 border-gray-200 pb-2">Firmas Digitales</h2>
            
            {hasAnySignature() ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {renderSignatureBlock('Remitente', ecmr.ExchangedDocument?.ConsignorDigitalSignature)}
                {renderSignatureBlock('Transportista', ecmr.ExchangedDocument?.CarrierDigitalSignature)}
                {renderSignatureBlock('Destinatario', ecmr.ExchangedDocument?.ConsigneeDigitalSignature)}
              </div>
            ) : (
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <p className="font-medium">Este documento no ha sido firmado</p>
                <p className="text-sm mt-1">Para añadir firmas, utilice la opción "Firmar" desde la lista de documentos.</p>
                <Link 
                  to={`/sign/${id}`}
                  className="inline-block mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-4 rounded text-sm"
                >
                  Ir a firmar documento
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewECMR; 